import React, { Fragment, useContext, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { getNavigationList, NAVIGATION_LIST, userMenuItems } from "./helpers";
import { getFullName } from "../Helpers/Functions";
import { MobileNavLink } from "./components";
import { AuthContext } from "../Contexts/AuthContext";

/* Tailwind is used in markup; additional CSS below (plain CSS) provides animations.
   If you use a CSS file or global styles, include the CSS block there. */

export default function MobileHeader() {
    // FIX: Destructuring all required values from the local AuthContext
    const { token, user = {}, signOut, setIsLoginModal, membership = {} } = useContext(AuthContext);

    const [navigationList, setNavigationList] = useState(NAVIGATION_LIST);

    useEffect(() => {
        // compute navigation based on the current user & membership
        const newNav = getNavigationList({ user, membership });

        // avoid updating state if nav didn't actually change
        setNavigationList(prev => {
            try {
                if (JSON.stringify(prev) === JSON.stringify(newNav)) {
                    return prev; // no change → avoid setState
                }
            } catch (e) {
                // fallback: if stringify fails for some reason, still set
            }
            return newNav;
        });
        // depend only on the stable, primitive values that determine the nav
    }, [user?.role, membership?.status]);


    const navButtons = [
        {
            name: "Login",
            onClick: () => {
                setIsLoginModal(true);
                animatedClose();
            },
        },
    ];

    // ---- Animation helpers (id-based) ----
    function animatedOpen() {
        const dialog = document.getElementById("mobile-menu");
        if (!dialog) return;

        // If dialog already open, ensure classes are right
        // show modal / show then add class to inner panel to animate
        if (typeof dialog.showModal === "function" && !dialog.open) {
            dialog.showModal();
        } else if (typeof dialog.show === "function" && !dialog.open) {
            dialog.show();
        } else if (!dialog.open) {
            // fallback: if neither showModal/show exists, add 'open' class to body panel (not typical)
            // but we proceed anyway
        }

        // small timeout to ensure dialog is in DOM/open before adding class
        // (forces a frame so transition triggers)
        requestAnimationFrame(() => {
            const panel = document.querySelector("#mobile-menu .mobile-panel");
            if (panel) {
                panel.classList.remove("closing");
                panel.classList.add("open");
            }
            dialog.classList.add("backdrop-open");
        });
    }

    function animatedClose() {
        const dialog = document.getElementById("mobile-menu");
        if (!dialog || !dialog.open) return;

        const panel = document.querySelector("#mobile-menu .mobile-panel");
        if (!panel) {
            // fallback: just close immediately
            if (dialog.open) dialog.close();
            dialog.classList.remove("backdrop-open");
            return;
        }

        // Add closing class to animate slide-out
        panel.classList.remove("open");
        panel.classList.add("closing");
        dialog.classList.remove("backdrop-open");

        // Wait for transition end on the panel, then close the dialog
        const onTransitionEnd = (e) => {
            // ensure we only respond to the transform transition
            if (
                e.target !== panel ||
                (e.propertyName && e.propertyName !== "transform")
            )
                return;
            panel.removeEventListener("transitionend", onTransitionEnd);
            if (dialog.open) dialog.close();
            // cleanup classes
            panel.classList.remove("closing");
        };

        panel.addEventListener("transitionend", onTransitionEnd);

        // Safety fallback: if transitionend doesn't fire, force-close after 350ms
        setTimeout(() => {
            if (panel) {
                panel.removeEventListener("transitionend", onTransitionEnd);
                if (dialog.open) dialog.close();
                panel.classList.remove("closing");
            }
        }, 450);
    }

    const toggleMobileMenu = () => {
        const dialog = document.getElementById("mobile-menu");
        if (!dialog) return;
        if (dialog.open) animatedClose();
        else animatedOpen();
    };

    useEffect(() => {
        const dialog = document.getElementById("mobile-menu");
        if (!dialog) return;

        const onKeyDown = (e) => {
            if (e.key === "Escape") animatedClose();
        };

        const onClickOutside = (e) => {
            // click on the dialog backdrop (the dialog element itself) should close
            if (e.target === dialog) animatedClose();
        };

        // If user presses the native 'cancel' event (some browsers fire it on ESC), handle it
        const onCancel = (ev) => {
            ev.preventDefault(); // prevent immediate close; we animate instead
            animatedClose();
        };

        document.addEventListener("keydown", onKeyDown);
        dialog.addEventListener("click", onClickOutside);
        dialog.addEventListener("cancel", onCancel);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            dialog.removeEventListener("click", onClickOutside);
            dialog.removeEventListener("cancel", onCancel);
        };
    }, []); // run once

    const userName = getFullName({ first_name: user?.first_name, last_name: user?.last_name }) || "";

    return (
        <Fragment>
            {/* Mobile Menu Button */}
            <button
                id="side-panel-button"
                onClick={toggleMobileMenu}
                className="lg:hidden text-white focus:outline-none rounded-2xl rounded-tr-md border-2 border-white px-4 py-2"
                aria-label="Open mobile menu"
            >
                <img
                    className="w-7 h-7"
                    src="/assets/icons/bar-icon.png"
                    alt="nav-bar-icon"
                />
            </button>

            {/* DIALOG: animation controlled via CSS classes on .mobile-panel */}
            <dialog
                id="mobile-menu"
                className="m-0 p-0 backdrop:bg-transparent lg:hidden"
            >
                {/* Backdrop area (dialog itself) -> click on this closes */}
                <div
                    tabIndex="0"
                    className="fixed inset-0 focus:outline focus:outline-0"
                >
                    {/* Sliding panel */}
                    <div className="mobile-panel fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-[#171A26] p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10 bg-gradient-radial dark:sm:ring-gray-100/10">
                        <div className="flex items-center justify-between">
                            <Link to="/" className="-m-1.5 p-1.5" onClick={animatedClose}>
                                <span className="sr-only">FWPI</span>
                                <img
                                    src="/assets/FWPI-logo.png"
                                    alt="FWPI Logo"
                                    className="w-30 h-8"
                                />
                            </Link>
                            <button
                                type="button"
                                onClick={animatedClose}
                                className="-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-gray-200"
                                aria-label="Close menu"
                            >
                                <span className="sr-only">Close menu</span>
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    data-slot="icon"
                                    aria-hidden="true"
                                    className="size-6"
                                >
                                    <path
                                        d="M6 18 18 6M6 6l12 12"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>

                        <div className="mt-6 flow-root">
                            <div className="-my-6 divide-y divide-gray-500/10">
                                <div className="space-y-2 py-6">
                                    {navigationList.map((navDetails, index) => (
                                        <MobileNavLink
                                            key={index}
                                            {...navDetails}
                                            onNavigate={animatedClose}
                                        />
                                    ))}
                                </div>

                                {/* Auth-based buttons */}
                                <div className="py-6 flex flex-col gap-3">
                                    {token ? (
                                        <>
                                            <div className="block -mx-3 px-3 py-2 text-lg font-bold text-white mb-2 border-b border-white/30">
                                                Hello, {user?.name || "Guest User"}
                                            </div>

                                            {userMenuItems(user?.role).map((item, index) =>
                                                item.isLink ? (
                                                    <NavLink
                                                        key={index}
                                                        to={item.path}
                                                        onClick={() => {
                                                            document
                                                                .getElementById("side-panel-button")
                                                                ?.click();
                                                            animatedClose();
                                                        }}
                                                        className="font-inter block w-full -mx-3 rounded-lg px-3 py-2 text-sm/7 font-semibold text-white hover:bg-white/5 hover:!text-gray-400 focus:!text-gray-400"
                                                    >
                                                        {item.name}
                                                    </NavLink>
                                                ) : (
                                                    <button
                                                        key={index}
                                                        onClick={() => {
                                                            document
                                                                .getElementById("side-panel-button")
                                                                ?.click();
                                                            signOut();
                                                            animatedClose();
                                                        }}
                                                        className="font-inter block w-full px-5 py-2 rounded-[10px] border border-red-500 text-white text-sm/7 font-medium hover:bg-red-600 transition mt-3"
                                                    >
                                                        {item.name}
                                                    </button>
                                                )
                                            )}
                                        </>
                                    ) : (
                                        navButtons.map((buttonDetails, index) => (
                                            <button
                                                key={index}
                                                onClick={() => buttonDetails?.onClick?.()}
                                                className="font-inter block w-full px-5 py-2 rounded-[10px] border border-white text-white text-sm/7 font-medium lg:text-lg hover:bg-white hover:!text-gray-400 focus:!text-gray-400"
                                            >
                                                {buttonDetails.name}
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </dialog>

            {/* Inline CSS for animations - move to your global CSS if you prefer */}
            <style>{`
                    /* Backdrop fade handled by dialog.backdrop-open class */
                    #mobile-menu {
                    /* ensure dialog covers viewport for backdrop clicks */
                    padding: 0;
                    }

                    /* When we want the backdrop visible (dark overlay), add this class */
                    #mobile-menu.backdrop-open::backdrop {
                    background: rgba(0,0,0,0.4);
                    backdrop-filter: blur(2px);
                    }

                    /* Panel default: off-screen (translated fully to right) */
                    #mobile-menu .mobile-panel {
                    transform: translateX(100%);
                    transition: transform 320ms cubic-bezier(.2,.9,.2,1);
                    will-change: transform;
                    }

                    /* When panel should be visible, slide to zero */
                    #mobile-menu .mobile-panel.open {
                    transform: translateX(0);
                    }

                    /* Closing state: slide out to right */
                    #mobile-menu .mobile-panel.closing {
                    transform: translateX(100%);
                    }

                    /* Slightly reduce focus outline flash on mobile */
                    #mobile-menu .mobile-panel:focus { outline: none; }

                    /* small responsive tweak: panel width */
                    @media (min-width: 640px) {
                    #mobile-menu .mobile-panel {
                        width: 420px;
                        right: 0;
                    }
                    }
      `}</style>
        </Fragment>
    );
}
